import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import moment from 'moment';
import { Skeleton, Timeline, Badge, NumberFormatter } from '@mantine/core';
import { CreditCard, RefreshCw, CheckCircle, AlertCircle, Clock, User, Mail } from 'lucide-react';
import { IndianRupee } from 'lucide-react';

import { EDrawer } from 'libs/ui';
import { useHotelData } from 'libs/data-access';
import { humanizeText, parseLocalDateTime, showToast } from 'utils/utility';

interface IRefundStatusItem {
    refundStatus: string;
    createdAt: string;
};

interface IRefundStatusResponse {
    createdBy: number;
    name: string;
    email: string;
    userType: string;
    refundAmount: number;
    refundStatusProgressResponse: IRefundStatusItem[];
};

interface IProps {
    bookingId: number | string;
    opened: boolean;
    onClose: Dispatch<SetStateAction<boolean>>;
    onRefundStatusCheck?: (hasRefundProcess: boolean) => void;
};

export default function RefundStatusTimeline({ ...props }: IProps) {

    const { getHotelData } = useHotelData();

    const [refundStatusData, setRefundStatusData] = useState<IRefundStatusResponse>({
        createdBy: 0,
        name: "",
        email: "",
        userType: "",
        refundAmount: 0,
        refundStatusProgressResponse: []
    });
    const [isLoadingStatuses, setIsLoadingStatuses] = useState<boolean>(false);


    useEffect(() => {
        if (props.opened) {
            getRefundStatuses();
        } else if (props.bookingId) {
            // Even if drawer is not open, check refund status once to inform parent
            checkRefundStatus();
        }
    }, [props.opened, props.bookingId]);


    // API CALLS
    // Check refund status without showing loading state
    const checkRefundStatus = async () => {
        if (!props.bookingId) return;

        try {
            const response = await getHotelData(`/booking/${props.bookingId}/refund-status-progress`);

            if (response.isSuccess) {
                const data = response.data?.data;
                const hasRefundProcess = data &&
                    data.refundStatusProgressResponse &&
                    data.refundStatusProgressResponse.length > 0;

                // Inform parent component about refund status
                props.onRefundStatusCheck && props.onRefundStatusCheck(hasRefundProcess);

                // Update state just in case
                setRefundStatusData(data);
            };
        } catch (error) {
            console.log("Error checking refund status:", error);
        };
    };

    const getRefundStatuses = async () => {
        setIsLoadingStatuses(true);

        const response = await getHotelData(`/booking/${props.bookingId}/refund-status-progress`);

        if (response.isSuccess) {
            const data = response.data?.data;
            setRefundStatusData(data);

            // Inform parent component about refund status
            const hasRefundProcess = data &&
                data.refundStatusProgressResponse &&
                data.refundStatusProgressResponse.length > 0;
            props.onRefundStatusCheck && props.onRefundStatusCheck(hasRefundProcess);
        } else {
            console.log("Error while getting refund statuses: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while getting refund status timeline!"
            });
        };

        setIsLoadingStatuses(false);
    };


    return (
        <EDrawer
            size="md"
            opened={props.opened}
            onClose={() => props.onClose(false)}
            title={
                <div className="flex items-center gap-2">
                    <CreditCard size={24} />
                    <div className="font-semibold">Refund Status</div>
                </div>
            }
        >
            {
                isLoadingStatuses ?
                    <div className="flex flex-col gap-5">
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                    </div>
                    :
                    <div className="">
                        {refundStatusData && refundStatusData.refundStatusProgressResponse.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {/* Refund Amount Card */}
                                <div className="bg-blue-50 rounded-lg p-4 flex items-center">
                                    <div className="bg-blue-100 rounded-full p-2 mr-3">
                                        <IndianRupee size={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600 font-medium">Refund Amount</div>
                                        <div className="text-lg font-bold text-blue-700">
                                            <NumberFormatter
                                                thousandSeparator
                                                value={refundStatusData.refundAmount}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Processed By Card */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="text-xs text-gray-600 font-medium mb-2">Processed by</div>
                                    <div className="flex items-start gap-3 mb-1">
                                        <User size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm font-medium truncate">{refundStatusData.name || "N/A"}</div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Mail size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-gray-700 break-all overflow-hidden">
                                            {refundStatusData.email || "N/A"}
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <Badge size="sm" color="gray" variant="outline">
                                            {humanizeText(refundStatusData.userType)}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="border-t pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-4">Status Timeline</h3>
                            <Timeline bulletSize={24} lineWidth={2} radius={"md"}>
                                {
                                    refundStatusData.refundStatusProgressResponse.length > 0
                                        ?
                                        refundStatusData.refundStatusProgressResponse.map((status, index) => (
                                            <Timeline.Item
                                                key={index + "_refund_status"}
                                                lineVariant="solid"
                                                bullet={
                                                    status.refundStatus === "SUCCESS"
                                                        ?
                                                        <CheckCircle size={16} color="green" />
                                                        :
                                                        status.refundStatus === "FAILED"
                                                            ?
                                                            <AlertCircle size={16} color="red" />
                                                            :
                                                            status.refundStatus === "PENDING"
                                                                ?
                                                                <Clock size={16} color="orange" />
                                                                :
                                                                <RefreshCw size={16} color="blue" />
                                                }
                                                title={
                                                    <div className="flex items-center gap-2">
                                                        <span className={
                                                            `text-sm font-semibold ${status.refundStatus === "SUCCESS"
                                                                ?
                                                                "text-green-600"
                                                                :
                                                                status.refundStatus === "FAILED"
                                                                    ?
                                                                    "text-red-600"
                                                                    :
                                                                    status.refundStatus === "PENDING"
                                                                        ?
                                                                        "text-orange-600"
                                                                        :
                                                                        "text-blue-600"
                                                            }`
                                                        }>{humanizeText(status.refundStatus)}</span>
                                                        <span className="text-xs text-gray-500">{parseLocalDateTime(status.createdAt)}</span>
                                                    </div>
                                                }
                                            >
                                                <div>
                                                    <span className="text-xs text-slate-500">Refund status updated</span>
                                                </div>
                                            </Timeline.Item>
                                        )) : (
                                            <div className="text-center py-4 text-gray-500">
                                                No refund status updates available
                                            </div>
                                        )
                                }
                            </Timeline>
                        </div>
                    </div>
            }
        </EDrawer>
    );
} 